/**
 * Chip Collect To Pot Effect for Poker Reborn Effects Engine
 *
 * Animates chips flying from all PlayerPotViews to the TablePotView.
 * Used when a betting round ends (flop/turn/river).
 *
 * @see docs/screens/room/TABLE_POT.md - Table pot specification
 * @see docs/screens/room/PLAYER_POT.md - Player pot specification
 */

import { Container, Graphics, Sprite } from 'pixi.js';
import { TimedEffect } from '../../core/TimedEffect';
import type { EffectTarget } from '../../core/EffectTypes';
import { EffectPriority } from '../../core/EffectTypes';
import type { EffectOptions } from '../../core/Effect';
import { easeOutCubic } from '../../utils/easing';
import {
  DENOMINATIONS_DESC,
  getVisualChipCount,
  getChipDenominationsForAmount,
  createChipSprite,
  CHIP_FLYING_SCALE,
  getChipFlyingScale,
} from '../../../components/chips/ChipColors';
import { tableState } from '../../../state/TableState';
import { debugOverlay } from '../../../utils/DebugOverlay';

// =============================================================================
// TYPES
// =============================================================================

export interface PlayerPotSource {
  /** X position of player pot */
  x: number;
  /** Y position of player pot */
  y: number;
  /** Amount in this player pot */
  amount: number;
  /** Seat number (for identification) */
  seatNumber?: number | undefined;
}

export interface ChipCollectToPotData {
  /** The container to add flying chips to */
  container: Container;
  /** Array of player pots to collect from */
  playerPots: PlayerPotSource[];
  /** Target X (table pot position) */
  toX: number;
  /** Target Y (table pot position) */
  toY: number;
  /** Stagger delay between player pots (default: 50ms) */
  potStagger?: number | undefined;
  /** Stagger delay between chips in each pot (default: 30ms) */
  chipStagger?: number | undefined;
  /** Arc height for curved path (default: 25) */
  arcHeight?: number | undefined;
  /** Callback when animation completes */
  onComplete?: (() => void) | undefined;
  /** Current screen width for BBS scaling */
  screenWidth?: number | undefined;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_DURATION = 400; // ms per pot group
const DEFAULT_POT_STAGGER = 50; // ms between pots
const DEFAULT_CHIP_STAGGER = 30; // ms between chips
const DEFAULT_ARC_HEIGHT = 25;
const CHIP_RADIUS = 10;
const MAX_CHIPS_PER_POT = 5;

// =============================================================================
// FLYING CHIP GROUP
// =============================================================================

interface FlyingChipGroup {
  chips: Array<{
    displayObject: Sprite | Graphics;
    startX: number;
    startY: number;
    denomination: number;
  }>;
  sourceAmount: number;
  startDelay: number; // When this group starts animating
}

// =============================================================================
// CHIP COLLECT TO POT EFFECT
// =============================================================================

/**
 * Animates chips from all player pots to the table pot.
 *
 * Flow:
 * 1. Creates chip graphics at each player pot position
 * 2. Animates them sequentially (with stagger) to table pot
 * 3. Fades out on arrival
 * 4. Calls onComplete callback
 */
export class ChipCollectToPotEffect extends TimedEffect {
  private readonly collectData: ChipCollectToPotData;
  private chipGroups: FlyingChipGroup[] = [];
  private _effectStartTime = 0;
  private readonly flyingScale: number;

  constructor(
    target: EffectTarget,
    data: ChipCollectToPotData,
    options: EffectOptions = {}
  ) {
    // Calculate total duration based on number of pots
    const potStagger = data.potStagger ?? DEFAULT_POT_STAGGER;
    const totalDuration =
      DEFAULT_DURATION + (data.playerPots.length - 1) * potStagger;

    // Capture callbacks to merge with our cleanup
    const userCallbacks = options.callbacks ?? {};
    const userOnComplete = userCallbacks.onComplete;

    super('ChipCollectToPot', target, options.duration ?? totalDuration, {
      ...options,
      priority: options.priority ?? EffectPriority.NORMAL,
      callbacks: {
        ...userCallbacks,
        onComplete: () => {
          const elapsed = Date.now() - this._effectStartTime;
          debugOverlay.log(`📦 CollectToPot DONE [actual=${elapsed}ms, expected=${this.duration}ms]`);
          // Cleanup flying chips
          this.cleanup();
          // Call data callback
          data.onComplete?.();
          // Call user callback
          userOnComplete?.();
        },
      },
    });

    this.collectData = data;
    this.flyingScale = data.screenWidth ? getChipFlyingScale(data.screenWidth) : CHIP_FLYING_SCALE;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  protected override onPlay(): void {
    this._effectStartTime = Date.now();
    const { container, playerPots, potStagger, toX, toY } = this.collectData;
    const stagger = potStagger ?? DEFAULT_POT_STAGGER;

    debugOverlay.log(`📦 CollectToPot START: ${playerPots.length} pots → (${Math.round(toX)}, ${Math.round(toY)}) [duration=${this.duration}ms]`);

    // Clear player bets immediately so PlayerPotViews hide as chips start flying
    this.clearPlayerBets();

    // Create chip groups for each player pot
    this.chipGroups = [];

    playerPots.forEach((pot, potIndex) => {
      if (pot.amount <= 0) return;

      const chipCount = Math.min(getVisualChipCount(pot.amount), MAX_CHIPS_PER_POT);
      const denominations = getChipDenominationsForAmount(pot.amount, chipCount);
      const colors = this.getChipColors(pot.amount, chipCount);
      const startDelay = potIndex * stagger;

      const chips: FlyingChipGroup['chips'] = [];

      for (let i = 0; i < chipCount; i++) {
        const denomination = denominations[i] ?? 1;

        // Try to create sprite, fall back to graphics
        let displayObject: Sprite | Graphics;
        const sprite = createChipSprite(denomination);
        if (sprite) {
          displayObject = sprite;
        } else {
          // Fallback to graphics
          const graphics = new Graphics();
          const color = colors[i] ?? 0xcc0000;
          this.drawChip(graphics, color);
          displayObject = graphics;
        }

        // Small random offset for natural look
        const offsetX = (Math.random() - 0.5) * 6;
        const offsetY = (Math.random() - 0.5) * 6;
        const startX = pot.x + offsetX;
        const startY = pot.y + offsetY;

        displayObject.x = startX;
        displayObject.y = startY;
        displayObject.scale.set(0.6 * this.flyingScale); // Start small
        displayObject.visible = false; // Hidden until animation starts

        container.addChild(displayObject);

        chips.push({
          displayObject,
          startX,
          startY,
          denomination,
        });
      }

      this.chipGroups.push({
        chips,
        sourceAmount: pot.amount,
        startDelay,
      });

      debugOverlay.log(`   seat ${pot.seatNumber}: $${pot.amount} (${chips.length} chips) @ (${Math.round(pot.x)}, ${Math.round(pot.y)})`);
    });

    super.onPlay();
  }

  protected onTick(progress: number): void {
    const { toX, toY, arcHeight, chipStagger } = this.collectData;
    const arc = arcHeight ?? DEFAULT_ARC_HEIGHT;
    const chipDelay = chipStagger ?? DEFAULT_CHIP_STAGGER;

    const elapsed = progress * this.duration;

    this.chipGroups.forEach((group) => {
      // Skip groups that haven't started yet
      if (elapsed < group.startDelay) {
        return;
      }

      const groupElapsed = elapsed - group.startDelay;
      const groupDuration = DEFAULT_DURATION;

      group.chips.forEach((chip, chipIndex) => {
        // Calculate staggered progress for this chip within its group
        const chipStartTime = chipIndex * chipDelay;
        const chipElapsed = groupElapsed - chipStartTime;

        if (chipElapsed < 0) {
          chip.displayObject.visible = false;
          return;
        }

        const availableDuration = groupDuration - chipStartTime;
        const chipProgress = Math.min(1, chipElapsed / availableDuration);

        chip.displayObject.visible = true;

        // Apply easing
        const eased = easeOutCubic(chipProgress);

        // Interpolate position
        chip.displayObject.x = chip.startX + (toX - chip.startX) * eased;
        chip.displayObject.y = chip.startY + (toY - chip.startY) * eased;

        // Add arc for natural movement (parabolic)
        const arcOffset = -arc * 4 * chipProgress * (1 - chipProgress);
        chip.displayObject.y += arcOffset;

        // Scale: grow in at start, then stay at 1
        if (chipProgress < 0.15) {
          chip.displayObject.scale.set((0.6 + chipProgress * 2.67) * this.flyingScale);
        } else {
          chip.displayObject.scale.set(this.flyingScale);
        }

        // Fade out near end
        if (chipProgress > 0.85) {
          chip.displayObject.alpha = 1 - (chipProgress - 0.85) * 6.67;
        } else {
          chip.displayObject.alpha = 1;
        }
      });
    });
  }

  protected override onCancel(): void {
    super.onCancel();
    this.cleanup();
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  private drawChip(graphics: Graphics, color: number): void {
    // Shadow
    graphics.circle(1, 1, CHIP_RADIUS);
    graphics.fill({ color: 0x000000, alpha: 0.3 });

    // Main chip body
    graphics.circle(0, 0, CHIP_RADIUS);
    graphics.fill(color);

    // Edge stripe pattern (2 stripes for small chips)
    const stripeOffset = CHIP_RADIUS * 0.65;
    graphics.circle(-stripeOffset, 0, CHIP_RADIUS * 0.15);
    graphics.fill(0xffffff);
    graphics.circle(stripeOffset, 0, CHIP_RADIUS * 0.15);
    graphics.fill(0xffffff);

    // Border
    graphics.circle(0, 0, CHIP_RADIUS);
    graphics.stroke({ width: 1, color: 0x000000 });

    // Highlight
    graphics.circle(-CHIP_RADIUS * 0.3, -CHIP_RADIUS * 0.3, CHIP_RADIUS * 0.2);
    graphics.fill({ color: 0xffffff, alpha: 0.4 });
  }

  private getChipColors(amount: number, count: number): number[] {
    const colors: number[] = [];

    // Use denominations that make sense for the amount
    let remaining = amount;
    for (const denom of DENOMINATIONS_DESC) {
      while (remaining >= denom.value && colors.length < count) {
        colors.push(denom.color);
        remaining -= denom.value;
      }
    }

    // Fill remaining with lowest denomination
    while (colors.length < count) {
      colors.push(DENOMINATIONS_DESC[DENOMINATIONS_DESC.length - 1]?.color ?? 0xffffff);
    }

    return colors;
  }

  private cleanup(): void {
    const { container } = this.collectData;

    for (const group of this.chipGroups) {
      for (const chip of group.chips) {
        container.removeChild(chip.displayObject);
        chip.displayObject.destroy();
      }
    }

    this.chipGroups = [];
  }

  /**
   * Clear player bets in TableState so PlayerPotViews hide immediately.
   * This is called when the chip collection animation starts.
   *
   * IMPORTANT: Only subtracts the captured bet amounts, not the entire bet.
   * This handles the case where new bets accumulated during animation delays.
   */
  private clearPlayerBets(): void {
    const { playerPots } = this.collectData;
    const currentPlayers = tableState.players.get();
    const updatedPlayers = new Map(currentPlayers);

    // Debug: log what's being cleared (suspended - too noisy)
    // import('@/utils/DebugOverlay').then(({ debugOverlay }) => {
    //   const cleared: string[] = [];
    //   for (const pot of playerPots) {
    //     if (pot.seatNumber !== undefined) {
    //       const player = currentPlayers.get(pot.seatNumber);
    //       if (player) {
    //         const newBet = Math.max(0, player.bet - pot.amount);
    //         if (pot.amount > 0) {
    //           cleared.push(`seat ${pot.seatNumber}: ${player.bet}→${newBet} (-${pot.amount})`);
    //         }
    //       }
    //     }
    //   }
    //   if (cleared.length > 0) {
    //     debugOverlay.log(`CLEAR BETS: ${cleared.join(', ')}`);
    //   }
    // });

    for (const pot of playerPots) {
      if (pot.seatNumber !== undefined) {
        const player = updatedPlayers.get(pot.seatNumber);
        if (player && pot.amount > 0) {
          // Subtract only the captured amount, not the entire bet
          // This preserves new bets that accumulated during delays
          const newBet = Math.max(0, player.bet - pot.amount);
          updatedPlayers.set(pot.seatNumber, {
            ...player,
            bet: newBet,
          });
        }
      }
    }

    // Update state - this triggers PlayerPotView subscriptions to hide
    tableState.players.set(updatedPlayers);
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Create a ChipCollectToPotEffect from cue data.
 */
export function createChipCollectToPotEffect(
  target: EffectTarget,
  data: Record<string, unknown>,
  options: EffectOptions = {}
): ChipCollectToPotEffect | null {
  const container = data['container'];
  if (!(container instanceof Container)) {
    return null;
  }

  const playerPots = data['playerPots'];
  if (!Array.isArray(playerPots)) {
    return null;
  }

  const collectData: ChipCollectToPotData = {
    container,
    playerPots: playerPots.map((pot) => ({
      x: (pot as { x?: number }).x ?? 0,
      y: (pot as { y?: number }).y ?? 0,
      amount: (pot as { amount?: number }).amount ?? 0,
      seatNumber: (pot as { seatNumber?: number }).seatNumber,
    })),
    toX: (data['toX'] as number) ?? 0,
    toY: (data['toY'] as number) ?? 0,
    potStagger: data['potStagger'] as number | undefined,
    chipStagger: data['chipStagger'] as number | undefined,
    arcHeight: data['arcHeight'] as number | undefined,
    onComplete: data['onComplete'] as (() => void) | undefined,
    screenWidth: data['screenWidth'] as number | undefined,
  };

  return new ChipCollectToPotEffect(target, collectData, options);
}
